<script setup>
  import { computed } from 'vue'
  import MultistepInput from '@/components/Form/MultistepInput.vue'
  import MultistepFormActions from '@/components/Form/MultistepFormActions.vue'

  const emit = defineEmits(['updateValue', 'back', 'next', 'submitStep'])
  const props = defineProps({
    data: {
      type: Object,
      required: true
    },
    values: {
      type: Object,
      default: () => ({})
    },
    canGoBack: {
      type: Boolean,
      default: false
    },
    isLastStep: {
      type: Boolean,
      default: false
    },
    nextLabel: {
      type: String,
      required: true
    },
    previousLabel: {
      type: String,
      required: true
    },
    submitLabel: {
      type: String,
      required: true
    },
    stepError: {
      type: Boolean,
      default: false
    },
    stepErrorMessage: {
      type: String,
      default: ''
    }
  })

  const isInvoiceSeparate = computed({
    get: () => Boolean(props.values.invoice_address),
    set: (value) => {
      emit('updateValue', 'invoice_address', value)
    }
  })

  const getHeadline = computed(() => {
    let text = 'Empty'
    props.data.elements.forEach((element) => {
      if (element.type === 'webform_markup') {
        text = element.markup
      }
    })
    return text
  })

  const formSchema = computed(() => {
    return props.data.elements
  })

  const invoiceCheckbox = computed(() => {
    return formSchema.value.filter((field) => {
      return field.name === 'invoice_address'
    })
  })

  const invoiceFields = computed(() => {
    return formSchema.value[2]
  })

  const privacyCheckboxes = computed(() => {
    return formSchema.value.filter((field) => {
      return field.name === 'agb' || field.name === 'privacy' || field.name === 'coupon'
    })
  })

  const updateValue = (field, value) => {
    emit('updateValue', field, value)
  }

  const handleNextStep = () => {
    emit('next')
  }

  const handleSubmitStep = () => {
    emit('submitStep')
  }
</script>

<template>
  <div
    class="invoice-form"
  >
    <GridLayout
      hasGap
    >
      <LeftHeadline
        :text="getHeadline"
      />
      <div
        class="invoice-form-form lg:col-span-8 "
      >
        <div
          class="invoice-form-invoice-fields flex flex-col lg:grid lg:grid-cols-2 ~gap-4/8"
        >
          <div class="invoice-address-checkbox lg:col-span-2">
            <FormKit
              v-model="isInvoiceSeparate"
              messageClass="text-danger min-h-10 text-xs mt-2"
              decoratorClass="bg-primary"
              wrapperClass="mt-5 flex flex-row items-start lg:items-center"
              inputClass="~w-5/7 ~h-5/7 bg-transparent ~mr-3/4 border-headline"
              type="checkbox"
              :name="invoiceCheckbox.name"
            >
              <template #label>
                <span
                  class="text-headline opacity-60 ~text-xs/base"
                >
                  abweichende Rechnungsadresse
                </span>
              </template>
            </FormKit>
          </div>
          <div
            class="invoice-address-fields flex flex-col lg:col-span-2 lg:grid lg:grid-cols-2 ~gap-4/8"
            v-if="isInvoiceSeparate"
            :class="isInvoiceSeparate ? 'py-8' : ''"
          >
            <MultistepInput
              v-for="(field, key) in invoiceFields"
              @updateValue="updateValue"
              :key="key"
              :data="field"
              :name="field.name"
              :model-value="values[field.name]"
            />
          </div>
        </div>

        <div
          class="invoice-form-invoice-field flex flex-col lg:grid lg:grid-cols-2 ~gap-4/8"
        >
          <MultistepInput
          v-for="(field, key) in privacyCheckboxes"
          @updateValue="updateValue"
          :key="key"
          :data="field"
          :name="field.name"
          :model-value="values[field.name]"
        />
        </div>

        <div class="step-headline col-span-2">
          <MultistepFormActions
            :can-go-back="canGoBack"
            :is-last-step="isLastStep"
            :next-label="nextLabel"
            :previous-label="previousLabel"
            :submit-label="submitLabel"
            :step-error="stepError"
            :step-error-message="stepErrorMessage"
            @back="emit('back')"
            @next="handleNextStep"
            @submit="handleSubmitStep"
          />
        </div>
      </div>
    </GridLayout>
  </div>

</template>

<style lang="scss" scoped>
.step-headline {
  @apply mt-6 flex flex-col lg:flex-row lg:justify-end mb-8;
}

</style>
